import { decode } from 'he'
import {
  Editor,
  Element,
  Range,
  Text,
  Transforms,
  Node as SlateNode,
  Path,
} from 'slate'

const HeadingTypes = ['h2', 'h3', 'h4']

export function withAutoParagraphAfterHeading(editor) {
  const { insertBreak } = editor

  editor.insertBreak = () => {
    // Only handle plain Enter (Shift+Enter should keep a soft break)
    if (!editor.selection) return insertBreak()

    const [headingEntry] = Editor.nodes(editor, {
      match: (n) => Element.isElement(n) && HeadingTypes.includes(n.type),
    })

    if (!headingEntry) {
      return insertBreak()
    }

    // Let Slate create the next block and convert it to a paragraph.
    insertBreak()

    Transforms.setNodes(
      editor,
      { type: 'paragraph' },
      { match: (n) => Element.isElement(n) && Editor.isBlock(editor, n) }
    )
  }

  return editor
}

export function withNormalizedHtmlEntities(editor) {
  const { normalizeNode } = editor

  editor.normalizeNode = (entry) => {
    const [node, path] = entry

    if (Text.isText(node)) {
      const newText = decode(node.text).replace(/\u00A0/g, ' ')

      if (newText !== node.text) {
        Transforms.insertText(editor, newText, { at: path })
        return
      }
    }

    normalizeNode(entry)
  }

  return editor
}

export function withLinks(editor) {
  const { isInline: original } = editor

  return Object.assign(editor, {
    isInline: (element) => element.type === 'link' || original(element),
  })
}

export function withSingleWord(editor) {
  const { normalizeNode } = editor

  editor.normalizeNode = ([node, path]) => {
    // Prevent top level from being split into multiple nodes with the enter key.
    if (Editor.isEditor(node)) {
      for (const [child, childPath] of SlateNode.children(editor, path, {
        reverse: true,
      })) {
        if (Element.isElement(child)) {
          Transforms.mergeNodes(editor, { at: childPath })
          return
        }
      }
    }

    // Strip any whitespace from text leaf nodes
    if (Text.isText(node)) {
      const nonWhitespaceText = node.text.replace(/\s/g, '')
      if (nonWhitespaceText !== node.text) {
        Transforms.insertText(editor, nonWhitespaceText, { at: path })
        return
      }
    }

    return normalizeNode([node, path])
  }

  return editor
}

export function withNormalizedLists(editor) {
  const { normalizeNode } = editor

  editor.normalizeNode = ([node, path]) => {
    if (Element.isElement(node) && node.type === 'unordered-list') {
      for (const [child, childPath] of SlateNode.children(editor, path)) {
        if (!Element.isElement(child)) continue

        if (child.type === 'unordered-list') {
          Transforms.unwrapNodes(editor, { at: childPath })
          return
        }

        if (child.type !== 'list-item') {
          Transforms.setNodes(editor, { type: 'list-item' }, { at: childPath })
          return
        }
      }
    }

    if (Element.isElement(node) && node.type === 'list-item') {
      if (SlateNode.parent(editor, path).type !== 'unordered-list') {
        Transforms.wrapNodes(
          editor,
          {
            type: 'unordered-list',
          },
          {
            at: Path.parent(path),
            match: (n) => Element.isElement(n) && n.type === 'list-item',
          }
        )
        return
      }
    }

    return normalizeNode([node, path])
  }

  return editor
}

export function withFlatText(editor) {
  const { normalizeNode } = editor

  editor.normalizeNode = ([node, path]) => {
    if (Element.isElement(node)) {
      if (node.type !== 'paragraph') {
        Transforms.setNodes(editor, { type: 'paragraph' }, { at: path })
        return
      }

      for (const [child, childPath] of SlateNode.children(editor, path)) {
        if (Element.isElement(child)) {
          Transforms.unwrapNodes(editor, { at: childPath })
          return
        }
      }
    }

    return normalizeNode([node, path])
  }

  return editor
}

export function deserialize(sourceBody) {
  try {
    return JSON.parse(sourceBody)
  } catch {
    if (sourceBody.startsWith('[') || sourceBody.startsWith('{')) {
      return [
        {
          type: 'paragraph',
          children: [{ text: '(missing)' }],
        },
      ]
    }

    return deserializeDomNode(
      new DOMParser().parseFromString(sourceBody, 'text/html').body
    )
  }
}

function deserializeDomNode(domNode) {
  switch (domNode.nodeType) {
    case Node.ELEMENT_NODE:
      return deserializeDomElement(domNode)
    case Node.TEXT_NODE:
      return deserializeDomText(domNode)
    default:
      throw 'Expected DOM node to be either Element or Text'
  }
}

function deserializeDomElement(domElement) {
  if (domElement.childNodes.length <= 0) {
    // Slate cannot handle empty elements, so we add an empty text.
    domElement.appendChild(domElement.ownerDocument.createTextNode(''))
  }

  const children = Array.from(domElement.childNodes).map(deserializeDomNode)

  switch (domElement.tagName) {
    case 'A':
      return {
        type: 'link',
        url: domElement.getAttribute('href') || '',
        children,
      }
    case 'BODY':
      return [...children]
    case 'LI':
      return { type: 'list-item', children }
    case 'P':
      return { type: 'paragraph', children }
    case 'UL':
      return { type: 'unordered-list', children }
    case 'H2':
      return { type: 'h2', children }
    case 'H3':
      return { type: 'h3', children }
    case 'H4':
      return { type: 'h4', children }
    default:
      throw `Unexpected tag name "${domElement.tagName}"`
  }
}

function deserializeDomText(domText) {
  return { text: domText.data }
}

export function serialize(slateNodes) {
  return JSON.stringify(slateNodes)
}

export function isElementAt(editor, type, at) {
  const entries = Editor.nodes(editor, {
    at,
    match: (node) => Element.isElement(node) && node.type === type,
  })

  return !entries.next().done // at least one entry found
}

export function elementTypeAt(editor, at) {
  const elements = Editor.nodes(editor, {
    at,
    match: (node) => Element.isElement(node),
    mode: 'highest',
  })

  const types = Object.keys(
    elements.reduce((acc, [element, _path]) => {
      acc[element.type] = true
      return acc
    }, {})
  )

  return types.length === 1 ? types[0] : 'multiple'
}

export function createLink(editor) {
  if (Range.isCollapsed(editor.selection)) return

  Transforms.unwrapNodes(editor, {
    match: (node) => Element.isElement(node) && node.type === 'link',
    split: true,
  })

  Transforms.wrapNodes(
    editor,
    {
      type: 'link',
      children: [],
    },
    {
      split: true,
      match: (node) => Text.isText(node) && node.text !== '',
    }
  )
}

export function removeLink(editor) {
  Transforms.unwrapNodes(editor, {
    match: (node) => Element.isElement(node) && node.type === 'link',
    split: true,
    mode: 'all',
  })
}

export function getLinkUrl(editor, at) {
  const entries = Editor.nodes(editor, {
    at,
    match: (node) => Element.isElement(node) && node.type === 'link',
  })

  let same = true
  let url = null
  for (const [node, _path] of entries) {
    if (url === null) {
      url = node.url
    } else if (url !== node.url) {
      same = false
      break
    }
  }

  return [same, url || '']
}

export function setLinkUrl(editor, url, at) {
  Transforms.setNodes(
    editor,
    { url },
    {
      at,
      match: (node) => Element.isElement(node) && node.type === 'link',
    }
  )
}

function removeAllUnorderedLists(editor) {
  Transforms.unwrapNodes(editor, {
    match: (node) => Element.isElement(node) && node.type === 'unordered-list',
    split: true,
  })

  Transforms.setNodes(
    editor,
    {
      type: 'paragraph',
    },
    {
      match: (node) => Element.isElement(node) && node.type === 'list-item',
    }
  )
}

function removeAllHeadings(editor) {
  Transforms.setNodes(
    editor,
    {
      type: 'paragraph',
    },
    {
      match: (node) =>
        Element.isElement(node) && HeadingTypes.includes(node.type),
    }
  )
}

function flattenAllToParagraphs(editor) {
  removeAllUnorderedLists(editor)
  removeAllHeadings(editor)
}

function convertParagraphsTo(editor, to) {
  Transforms.setNodes(
    editor,
    {
      type: to,
    },
    {
      match: (node) => Element.isElement(node) && node.type === 'paragraph',
    }
  )
}

export function changeToParagraph(editor) {
  Editor.withoutNormalizing(editor, () => {
    flattenAllToParagraphs(editor)
  })
}

export function changeToUnorderedList(editor) {
  Editor.withoutNormalizing(editor, () => {
    flattenAllToParagraphs(editor)
    convertParagraphsTo(editor, 'list-item')

    Transforms.wrapNodes(editor, {
      type: 'unordered-list',
      children: [],
    })
  })
}

export function changeToHeading(editor, type) {
  Editor.withoutNormalizing(editor, () => {
    flattenAllToParagraphs(editor)
    convertParagraphsTo(editor, type)
  })
}
